import React, { useCallback } from 'react';
import { Input } from '../../../../components/forms/Input';
import { FieldValues, UseFormReturn, useWatch, ValidateResult } from 'react-hook-form';
import { InputTypes } from 'constants/form';
import { FormModal } from 'components/containers/FormModal';
import { Toggle } from 'components/forms/Toggle';
import { Select } from 'components/forms/Select';
import { SocialProfileSelector } from 'components/socialProfiles/SocialProfileSelector';
import { Checkbox } from 'components/forms/Checkbox';
import pluralize from 'pluralize';
import classes from './CreateEditImporterModal.module.scss';
import { SelectItem } from 'components/forms/Select/Select.view';
import { getCategorySelectTemplates } from 'modules/Publish/Categories/Categories.helpers';
import { AutoImportQueryResult, CategoriesResult } from '../AutoImporters.view';
import { SelectedSocialProfileValue } from 'components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { FooterContentTemplate } from 'components/containers/Modal/Modal.view';
import { Button } from 'components/actions/Button';
import { ButtonColorsEnum } from 'constants/button-colors';
import { ValidationMessages } from 'constants/validation-messages';
import { AutoImportPostTiming, GetSocialProfilesQuery } from 'models/graphql';
import classNames from 'classnames';
import { CreateEditImportFormValues, newRssImportType } from './CreateEditImporterModal.controller';

export type CreateEditImporterModalViewProps = {
  onClose: () => void;
  useFormInstance: UseFormReturn<CreateEditImportFormValues>;
  onSubmit: () => void;
  loading: boolean;
  autoImporter?: AutoImportQueryResult;
  newRssAutoImport?: newRssImportType;
  categories: CategoriesResult[];
  socialProfiles: GetSocialProfilesQuery['workspace']['socialProfiles'][number][];
  selectedProfiles: SelectedSocialProfileValue[];
};

const whereImportOptions = [
  { label: 'Draft', value: AutoImportPostTiming.DRAFT },
  { label: 'Top of Queue', value: AutoImportPostTiming.QUEUE_TOP },
  { label: 'Bottom of Queue', value: AutoImportPostTiming.QUEUE_BOTTOM }
];
const saveOptions = [...whereImportOptions, { label: 'Publish ASAP', value: AutoImportPostTiming.NOW }];

const qtyTimesItems: SelectItem[] = Array(50)
  .fill(0)
  .map((_, index) => {
    const value = index + 1;
    return { label: value.toString(), value };
  });

const SelectQtyTimes = ({
  formInstance,
  selectedValue,
  loading
}: {
  loading: boolean;
  formInstance: UseFormReturn;
  selectedValue: number | undefined;
}) => {
  const { control } = formInstance;
  const selectValue = useWatch({ control, name: 'qtyTimesPost' });
  const expireAfterCheckValue = useWatch({ control, name: 'expireAfterCheck' });
  return (
    <div className={classes.SelectQtyTimesContainer}>
      <Checkbox
        disabled={loading}
        label={'Expire after posting '}
        name={'expireAfterCheck'}
        size={'large'}
        formInstance={formInstance}
        rules={{}}
        checked={!!selectedValue}
      />
      <div className={classes.Dropdown}>
        <Select
          readonly={!expireAfterCheckValue || loading}
          hideDefaultSelectOption
          selectedValue={selectedValue}
          items={qtyTimesItems}
          name={'qtyTimesPost'}
          rules={{}}
          size={'large'}
          useFormInstance={formInstance}
        />
      </div>
      <div>{pluralize('time', parseInt(selectValue, 10))}</div>
    </div>
  );
};

const validateProfiles = (value: Array<string | false> | undefined): ValidateResult => {
  if (!value) {
    return ValidationMessages.autoImport.profiles.noProfile;
  }
  return value.filter(Boolean).length ? undefined : ValidationMessages.autoImport.profiles.noProfile;
};

const validateName = (value: string | undefined): ValidateResult => {
  if (!value || value.trim().length < 1) return ValidationMessages.name.required;
  const result = value.trim().replace(/\s+/g, ' ').length <= 191;
  if (!result) return ValidationMessages.discover.source.name.maxLength;
  return undefined;
};

export const CreateEditImporterModalView = ({
  useFormInstance,
  loading,
  onClose,
  autoImporter,
  newRssAutoImport,
  categories,
  socialProfiles,
  onSubmit,
  selectedProfiles
}: CreateEditImporterModalViewProps): JSX.Element => {
  const formInstance = useFormInstance as unknown as UseFormReturn;
  const mappedCategories: SelectItem[] = categories.map(category => ({
    label: category.name,
    value: category.id
  }));
  const [valueTemplate, itemTemplate] = getCategorySelectTemplates(categories);
  const refineSearchTermPrefixValue: string | undefined = useWatch({
    control: useFormInstance.control,
    name: 'refineSearchTermPrefix'
  });
  const refineSearchTermSuffixValue: string | undefined = useWatch({
    control: useFormInstance.control,
    name: 'refineSearchTermSuffix'
  });
  const shouldRefineSearchValue =
    useWatch({
      control: useFormInstance.control,
      name: 'shouldRefineSearch'
    }) === 'true';
  const shouldShowModifyArticleTitleValue =
    useWatch({
      control: useFormInstance.control,
      name: 'shouldModifyArticle'
    }) === 'true';
  const shouldShowEvergreenHelperValue =
    useWatch({
      control: useFormInstance.control,
      name: 'recycling'
    }) === 'true';
  const shouldShowImportSelectValue =
    useWatch({
      control: useFormInstance.control,
      name: 'shouldImportOldPosts'
    }) === 'true';

  const ModalFooter: FooterContentTemplate = useCallback(() => {
    return (
      <div className={classes.FooterContainer}>
        {!autoImporter && (
          <Checkbox
            readonly={loading}
            formInstance={formInstance}
            name={'pauseImport'}
            size={'large'}
            rules={{}}
            label={'Pause importing for now'}
          />
        )}
        <Button
          className={classNames(classes.ButtonModal, {
            [classes.ButtonModalEditMode]: autoImporter
          })}
          loading={loading}
          size={'large'}
          color={ButtonColorsEnum.secondary}
          variant={'solid'}
          type={'button'}
          action={onSubmit}>
          Save Auto-Importer
        </Button>
      </div>
    );
  }, [formInstance, loading, onSubmit, autoImporter]);

  const disabledInput = newRssAutoImport ? !!newRssAutoImport.url : true;

  const validatePnBoards = useCallback(
    (value: string[], originalData: FieldValues): ValidateResult => {
      const data = originalData as CreateEditImportFormValues;
      const selectedPnProfilesIndexes = data.profiles.flatMap((id, index) => {
        const pnProfile = socialProfiles.find(sp => sp.id === id && sp.__typename === 'PinterestSocialProfile');
        return pnProfile ? index : [];
      });
      // we create mappedProfilesToPnBoards and populate with false values, because our value (pinterest boards we need to validate)
      // might have undefined indexes since value might be undefined if the pn profile is not selected.
      // mappedProfilesToPnBoards is used to make sure we don't skip missing indexes
      const mappedProfilesToPnBoards = data.profiles.map(_ => false);
      const error = mappedProfilesToPnBoards.reduce((prev, _, index) => {
        if (prev || !selectedPnProfilesIndexes.includes(index)) return prev;
        return value[index] ? '' : ValidationMessages.autoImport.profiles.pinBoard;
      }, '');
      return error ? error : undefined;
    },
    [socialProfiles]
  );

  return (
    <FormModal
      showCloseIcon={!loading}
      formInstance={formInstance}
      loading={loading}
      onClose={onClose}
      onSubmit={onSubmit}
      size={'small'}
      submitText={'Save Auto-Importer'}
      title={autoImporter ? 'Edit Auto-Importer' : 'Add an Auto-Importer'}
      footerContent={ModalFooter}>
      <div className={classes.FormContainer}>
        <div className={`padding-bottom-small-20`}>
          <Input
            readonly={disabledInput || loading}
            formInstance={formInstance}
            name={'source'}
            type={InputTypes.text}
            size={'large'}
            label={`Source URL`}
            rules={{ required: ValidationMessages.autoImport.url.required }}
          />
        </div>
        <div className={`padding-bottom-small-20`}>
          <Input
            readonly={loading}
            formInstance={formInstance}
            name={'name'}
            type={InputTypes.text}
            size={'large'}
            label={'Name'}
            rules={{
              validate: validateName
            }}
          />
        </div>
        <div className={`padding-bottom-small-20`}>
          <Toggle
            disabled={loading}
            formInstance={formInstance}
            name={'shouldRefineSearch'}
            action={(newVal, complete) => {
              formInstance.setValue('shouldRefineSearch', `${newVal}`);
              complete();
            }}
            checked={!!autoImporter?.refineByTerm || !!newRssAutoImport?.term}
            label={`Refine by search terms`}
            rules={{}}
          />
          {shouldRefineSearchValue && (
            <div className={`padding-top-small-20`}>
              <Input
                readonly={loading}
                formInstance={formInstance}
                name={'refineSearchTerm'}
                type={InputTypes.text}
                size={'large'}
                rules={{ required: shouldRefineSearchValue && ValidationMessages.autoImport.refine.required }}
              />
            </div>
          )}
        </div>

        <div className={`padding-bottom-small-20`}>
          <Toggle
            disabled={loading}
            formInstance={formInstance}
            name={'shouldModifyArticle'}
            label={`Modify article titles`}
            rules={{}}
            action={(newVal, complete) => {
              formInstance.setValue('shouldModifyArticle', `${newVal}`);
              complete();
            }}
          />
          {shouldShowModifyArticleTitleValue && (
            <div className={'padding-top-small-20'}>
              <div className={'padding-bottom-small-20'}>
                <Input
                  readonly={loading}
                  formInstance={formInstance}
                  name={'refineSearchTermPrefix'}
                  type={InputTypes.text}
                  size={'large'}
                  label={"Add this text before article's title"}
                  rules={{
                    required:
                      shouldShowModifyArticleTitleValue &&
                      !refineSearchTermSuffixValue &&
                      ValidationMessages.autoImport.modify.required
                  }}
                />
              </div>
              <Input
                label={"Add this text after article's title"}
                readonly={loading}
                formInstance={formInstance}
                name={'refineSearchTermSuffix'}
                type={InputTypes.text}
                size={'large'}
                rules={{
                  required:
                    shouldShowModifyArticleTitleValue &&
                    !refineSearchTermPrefixValue &&
                    ValidationMessages.autoImport.modify.required
                }}
              />
            </div>
          )}
        </div>
        <div className={`padding-bottom-small-20`}>
          <Select
            readonly={loading}
            items={mappedCategories}
            name={'category'}
            size={'large'}
            rules={{ required: ValidationMessages.categoriesSelect.required }}
            label={'Import to this Category'}
            valueTemplate={valueTemplate}
            itemTemplate={itemTemplate}
            useFormInstance={formInstance}
          />
        </div>
        <div className={classNames(`padding-bottom-small-20`, classes.SocialProfileContainer)}>
          <SocialProfileSelector
            disabled={loading}
            usePinterestBoard={true}
            label={'Import to these Profiles'}
            rules={{ validate: validateProfiles }}
            selectItems={socialProfiles}
            useFormInstance={formInstance}
            formFieldName={'profiles'}
            selectAllFormName={'select_all_profiles'}
            pinterestRules={{ required: ValidationMessages.autoImport.profiles.pinBoard }}
            pinterestGroupRules={{ validate: validatePnBoards }}
            disabledProfileIds={[]}
            {...(selectedProfiles.length ? { selectedProfiles } : {})}
          />
        </div>
        <div className={`padding-bottom-small-20`}>
          <Select
            readonly={loading}
            items={saveOptions}
            name={'howSave'}
            size={'large'}
            rules={{ required: ValidationMessages.autoImport.saveOption.required }}
            label={'How to Save'}
            useFormInstance={formInstance}
          />
        </div>
        <div className={`padding-bottom-small-20`}>
          <Toggle
            checked={!!autoImporter?.recycle}
            disabled={loading}
            formInstance={formInstance}
            name={'recycling'}
            label={`Evergreen Recycling`}
            action={(newVal, complete) => {
              formInstance.setValue('recycling', `${newVal}`);
              complete();
            }}
            rules={{}}
            {...(shouldShowEvergreenHelperValue
              ? {
                  helper:
                    'After this post is published, it will automatically be re-added to the bottom of the queue and recycle based on your Posting Plan'
                }
              : {})}
          />
          {shouldShowEvergreenHelperValue && (
            <SelectQtyTimes
              formInstance={formInstance}
              selectedValue={autoImporter?.recycleMaxRepeats}
              loading={loading}
            />
          )}
        </div>
        {!autoImporter && (
          <div className={`padding-bottom-small-20`}>
            <Toggle
              disabled={loading}
              formInstance={formInstance}
              name={'shouldImportOldPosts'}
              label={`Import old Posts`}
              rules={{}}
              action={(newVal, complete) => {
                formInstance.setValue('shouldImportOldPosts', `${newVal}`);
                complete();
              }}
            />
          </div>
        )}
        {shouldShowImportSelectValue && (
          <div className={`padding-bottom-small-20`}>
            <Select
              readonly={loading}
              items={whereImportOptions}
              name={'whereImport'}
              size={'large'}
              rules={{ required: shouldShowImportSelectValue && ValidationMessages.autoImport.import.required }}
              label={'Where to Import'}
              useFormInstance={formInstance}
            />
          </div>
        )}
      </div>
    </FormModal>
  );
};
